import { NavLink } from "react-router";
import { useGlobalContext } from "app/store/GlobalContext";
import { useRootStore } from "store";
import { useCustomerId } from "hooks/use-customer-id";
import { usePermissions } from "hooks/use-permissions";

import "./CustomerNavLinks.scss";
import MenuDropdown from "components/menu/MenuDropdown/MenuDropdown";
import { useState } from "react";

type Props = {
  handleChangeCustomerNavigation: (customerId: string) => void;
  isOnMobile?: boolean;
};

export default function CustomerNavLinks({
  handleChangeCustomerNavigation,
  isOnMobile
}: Props) {
  const { handleNavigation } = useGlobalContext();
  const customerId = useCustomerId();
  const { myCustomers } = useRootStore((state) => state.customers);
  const { isAdmin, canViewInvoiceReport, canViewAllCustomers } = usePermissions();

  const [search, setSearch] = useState("");

  const hasMultipleOperations = myCustomers.customers.length >= 5;

  const handleMenuToggle = (isOpen: boolean) => {
    // Reset search when menu is collapsed
    if (!isOpen) {
      setSearch("");
    }
  };

  const searchMenuItem = {
    content: (
      <div className="customerSearchWrapper">
        <input
          placeholder="Sök kund..."
          type="text"
          value={search}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
    ),
    hasDivider: true,
    className: "p-0"
  };

  const customersItems = myCustomers.customers
    .filter(
      (c) =>
        search === "" ||
        c.customerName.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => (a.customerName > b.customerName ? 1 : -1));

  const menuItems = customersItems.map((customer) => {
    return {
      content: (
        <span
          className={
            customerId === customer.customerId ? "fw-bold" : ""
          }
        >
          {customer.customerName}
        </span>
      ),
      onClick: () => handleChangeCustomerNavigation(customer.customerId)
    };
  });

  const adminLink = (
    <>
      {isOnMobile ? (
        <div className="fullscreenMenuDivider" />
      ) : (
        <div className="headerDivider">|</div>
      )}

      <NavLink
        to="/admin"
        onClick={(e) => {
          handleNavigation({
            url: "/admin",
            eventTitle: "Admin",
            e: e
          });
        }}
        className={isOnMobile ? "fullscreenMenuLink" : ""}
      >
        <span>Admin</span>
      </NavLink>
    </>
  );

  const invoicesLink = (
    <>
      {isOnMobile ? (
        <div className="fullscreenMenuDivider" />
      ) : (
        <div className="headerDivider">|</div>
      )}
      <NavLink
        to="/customers/invoices"
        onClick={(e) => {
          handleNavigation({
            url: "/customers/invoices",
            eventTitle: "Invoices",
            e: e
          });
        }}
        className={isOnMobile ? "fullscreenMenuLink" : ""}
      >
        <span>Fakturor</span>
      </NavLink>
    </>
  );

  const allMenuItems = [
    ...(hasMultipleOperations ? [searchMenuItem] : []),
    ...menuItems,
    ...(search !== "" && customersItems.length === 0
      ? [
          {
            content: <p>Inga sökresultat på "{search}".</p>
          }
        ]
      : [])
  ];

  const renderCustomersDropdown = () => (
    <>
      <div className="pl-3 customerDropdown">
        <MenuDropdown
          toggleText=""
          hasCaret
          menuItems={allMenuItems}
          onToggle={handleMenuToggle}
        />
      </div>

      <div className="customerMenuFullscreen">
        <div className="fullscreenMenuDivider mb-4" />
        <h2 className="mobile-menu-header">Mina kunder </h2>
        {hasMultipleOperations && (
          <div className="customerSearchWrapper customerSearchWrapperRounded">
            <input
              placeholder="Sök verksamhet..."
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        )}
        <ul className="list-unstyled">
          {menuItems.map((customerItem, i) => (
            <li key={i} className="py-2">
              <a onClick={customerItem.onClick}>{customerItem.content}</a>
            </li>
          ))}

          {search !== "" && customersItems.length === 0 && (
            <p>Inga sökresultat på "{search}".</p>
          )}
        </ul>
        <div className="fullscreenMenuDivider mt-4" />
      </div>
    </>
  );

  return (
    <div className={isOnMobile ? "fullscreenMenuSection" : "customerNavLinks"}>
      <div className={isOnMobile ? "" : "d-flex align-items-center"}>
        <NavLink
          to="/customers/my"
          onClick={(e) => {
            handleNavigation({
              url: "/customers/my",
              eventTitle: "Mina kunder",
              e: e
            });
          }}
          className={isOnMobile ? "fullscreenMenuLink" : ""}
        >
          <span>Mina kunder</span>
        </NavLink>
        {!isOnMobile && renderCustomersDropdown()}
      </div>
      {canViewAllCustomers && (
        <NavLink
          to="/customers/all"
          onClick={(e) => {
            handleNavigation({
              url: "/customers/all",
              eventTitle: "Alla kunder",
              e: e
            });
          }}
          className={isOnMobile ? "fullscreenMenuLink" : ""}
        >
          <span>Alla kunder</span>
        </NavLink>
      )}
      {canViewInvoiceReport && invoicesLink}
      {isAdmin && adminLink}
      {isOnMobile && renderCustomersDropdown()}
    </div>
  );
}
